import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PainelMasterPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', senha: '' });
  const [clientes, setClientes] = useState([]);
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  const [showEditCliente, setShowEditCliente] = useState(null);
  const [showEditLogin, setShowEditLogin] = useState(null);
  const [formData, setFormData] = useState({
    nome_clinica: '',
    cnpj: '',
    telefone: '',
    email_contato: '',
    site: '',
    endereco: '',
    observacoes: '',
    login_email: '',
    login_senha: ''
  });
  const [loginUpdateData, setLoginUpdateData] = useState({
    novo_email: '',
    nova_senha: ''
  });
  const [activeTab, setActiveTab] = useState('clientes');
  const [submitting, setSubmitting] = useState(false);
  const [repairForm, setRepairForm] = useState({
    source_email: '',
    target_email: '',
    target_calendar_id_override: '',
    dry_run: true
  });
  const [repairResult, setRepairResult] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [targetInfo, setTargetInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState({ source: false, target: false });
  const [diagnostico, setDiagnostico] = useState(null);
  const [diagnosticoLoading, setDiagnosticoLoading] = useState(false);
  const [repairAllResult, setRepairAllResult] = useState(null);
  const [repairAllLoading, setRepairAllLoading] = useState(false);
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  // Transferencia Direta
  const [transferSource, setTransferSource] = useState('');
  const [transferTarget, setTransferTarget] = useState('');
  const [transferConsultas, setTransferConsultas] = useState([]);
  const [transferSelected, setTransferSelected] = useState(new Set());
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSourceInfo, setTransferSourceInfo] = useState(null);
  const [transferTargetInfo, setTransferTargetInfo] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  // Diagnostico Sync
  const [syncDiagEmail, setSyncDiagEmail] = useState('');
  const [syncDiagResult, setSyncDiagResult] = useState(null);
  // Backup & Restore
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupIncludeFiles, setBackupIncludeFiles] = useState(true);
  // Backfill retornos
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [restoreConfirmText, setRestoreConfirmText] = useState('');
  const [restoreResult, setRestoreResult] = useState(null);
  const [syncDiagLoading, setSyncDiagLoading] = useState(false);
  const [syncDiagFilter, setSyncDiagFilter] = useState('todos');
  const [syncFixLoading, setSyncFixLoading] = useState(false);
  const [syncFixingIds, setSyncFixingIds] = useState(new Set());

  useEffect(() => {
    const token = localStorage.getItem('master_token');
    if (token) {
      setIsAuthenticated(true);
      fetchClientes(token);
    }
    setLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/master/login`, loginForm);
      const token = response.data.access_token;
      localStorage.setItem('master_token', token);
      setIsAuthenticated(true);
      toast.success('Login realizado com sucesso!');
      fetchClientes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('master_token');
    setIsAuthenticated(false);
    setClientes([]);
    toast.success('Logout realizado com sucesso!');
  };

  const fetchClientes = async (token) => {
    try {
      const response = await axios.get(`${API}/master/clientes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    }
  };

  const runDiagnostico = async () => {
    setDiagnosticoLoading(true);
    setDiagnostico(null);
    setRepairAllResult(null);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.get(`${API}/master/diagnostico-tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostico(response.data);
      if (response.data.total_issues === 0) {
        toast.success('Nenhum problema encontrado! Todos os clientes estão com dados corretos.');
      } else {
        toast.error(`Encontrados ${response.data.total_issues} consultas com problemas!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar diagnóstico');
    } finally {
      setDiagnosticoLoading(false);
    }
  };

  const handleRepairAll = async (dryRun) => {
    setRepairAllLoading(true);
    setRepairAllResult(null);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/repair-all-tenants`, { dry_run: dryRun }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRepairAllResult(response.data);
      if (dryRun) {
        toast.info('Simulação concluída. Revise antes de executar.');
      } else {
        toast.success(`Reparo concluído: ${response.data.total_consultas_fixed} consultas corrigidas!`);
        runDiagnostico();
        fetchAuditLogs();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar reparo');
    } finally {
      setRepairAllLoading(false);
    }
  };

  const loadTransferConsultas = async (email) => {
    setTransferLoading(true);
    setTransferConsultas([]);
    setTransferSelected(new Set());
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/list-client-consultas`, { email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransferConsultas(response.data.consultas);
      setTransferSourceInfo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar consultas');
    } finally {
      setTransferLoading(false);
    }
  };

  const loadTransferTargetInfo = async (email) => {
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/tenant-info`, { email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransferTargetInfo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Usuário destino não encontrado');
      setTransferTargetInfo(null);
    }
  };

  const executeTransfer = async () => {
    if (transferSelected.size === 0) { toast.error('Selecione pelo menos uma consulta'); return; }
    if (!window.confirm(`Transferir ${transferSelected.size} consultas para ${transferTarget}?`)) return;
    setTransferLoading(true);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/transfer-consultas`, {
        source_email: transferSource,
        target_email: transferTarget,
        consulta_ids: Array.from(transferSelected)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      if (response.data.google_errors?.length > 0) {
        toast.warning(`Avisos Google Calendar: ${response.data.google_errors.join('; ')}`);
      }
      // Reload to update counts
      loadTransferConsultas(transferSource);
      loadTransferTargetInfo(transferTarget);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir');
    } finally {
      setTransferLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (transferSelected.size === transferConsultas.length) {
      setTransferSelected(new Set());
    } else {
      setTransferSelected(new Set(transferConsultas.map(c => c.id)));
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.get(`${API}/master/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setAuditLoading(false);
    }
  };

  // =========== Backup & Restore ===========
  const handleBackupDownload = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.get(`${API}/master/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_files: backupIncludeFiles },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `backup_agenda_mais_med_${now}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Backup baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const handlePreviewRestore = async () => {
    if (!restoreFile) {
      toast.error('Selecione um arquivo ZIP primeiro');
      return;
    }
    setPreviewLoading(true);
    setPreviewResult(null);
    setRestoreResult(null);
    try {
      const token = localStorage.getItem('master_token');
      const fd = new FormData();
      fd.append('file', restoreFile);
      const response = await axios.post(`${API}/master/backup/preview`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewResult(response.data);
      toast.success('Preview gerado. Revise antes de confirmar a restauração.');
    } catch (error) {
      toast.error('Erro ao analisar backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBackfillRetornos = async (apply) => {
    if (apply && !window.confirm('Executar backfill de retornos? Esta ação altera consultas no banco.')) return;
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(
        `${API}/master/backfill/retornos`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, params: { dry_run: !apply } }
      );
      setBackfillResult(response.data);
      toast.success(apply
        ? `Backfill aplicado: ${response.data.total_consultas_vinculadas} consultas vinculadas`
        : `Preview: ${response.data.total_consultas_vinculadas} consultas seriam vinculadas`);
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.detail || e.message));
    } finally { setBackfillLoading(false); }
  };


  const handleExecuteRestore = async () => {
    if (!restoreFile) { toast.error('Selecione um arquivo ZIP primeiro'); return; }
    if (restoreConfirmText !== 'RESTAURAR') {
      toast.error('Digite "RESTAURAR" exatamente para confirmar');
      return;
    }
    if (restoreMode === 'replace' &&
        !window.confirm('Modo REPLACE é destrutivo: apaga todas as coleções antes de restaurar. Tem certeza?')) {
      return;
    }
    setRestoreLoading(true);
    try {
      const token = localStorage.getItem('master_token');
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('mode', restoreMode);
      fd.append('confirm', 'RESTAURAR');
      const response = await axios.post(`${API}/master/backup/restore`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestoreResult(response.data);
      toast.success(`Restore concluído: ${response.data.total_inserted} inseridos, ${response.data.total_updated} atualizados.`);
      setRestoreConfirmText('');
    } catch (error) {
      toast.error('Erro ao restaurar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRestoreLoading(false);
    }
  };

  const cleanupDuplicatePacientes = async () => {
    if (!transferSource || !transferTarget) {
      toast.error('Preencha os emails de origem e destino');
      return;
    }
    if (!window.confirm(`Remover pacientes duplicados de ${transferSource} que já existem em ${transferTarget}?`)) return;
    setCleanupLoading(true);
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/cleanup-duplicate-pacientes`, {
        source_email: transferSource,
        target_email: transferTarget
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      if (response.data.kept?.length > 0) {
        toast.warning(`Mantidos: ${response.data.kept.join(', ')}`);
      }
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar pacientes duplicados');
    } finally {
      setCleanupLoading(false);
    }
  };





  const runSyncDiagnostico = async () => {
    if (!syncDiagEmail.trim()) {
      toast.error('Informe o email do cliente');
      return;
    }
    setSyncDiagLoading(true);
    setSyncDiagResult(null);
    setSyncFixingIds(new Set());
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/diagnostico-sync`, { email: syncDiagEmail.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncDiagResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar diagnóstico');
    } finally {
      setSyncDiagLoading(false);
    }
  };

  const handleForcarSync = async (eventIds) => {
    if (!syncDiagEmail.trim() || eventIds.length === 0) return;
    const isMultiple = eventIds.length > 1;
    if (isMultiple && !window.confirm(`Forçar sync de ${eventIds.length} eventos? Isso criará novas consultas no app.`)) return;
    
    if (isMultiple) setSyncFixLoading(true);
    else setSyncFixingIds(prev => new Set([...prev, ...eventIds]));
    
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/forcar-sync`, {
        email: syncDiagEmail.trim(),
        event_ids: eventIds
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const { total_criadas, total_erros, erros } = response.data;
      if (total_criadas > 0) toast.success(`${total_criadas} consulta(s) criada(s) com sucesso!`);
      if (total_erros > 0) toast.error(`${total_erros} erro(s): ${erros.map(e => e.motivo).join('; ')}`);
      
      // Re-run diagnostic to refresh the table
      runSyncDiagnostico();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao forçar sincronização');
    } finally {
      setSyncFixLoading(false);
      setSyncFixingIds(new Set());
    }
  };

  const fetchTenantInfo = async (email, type) => {
    if (!email.trim()) return;
    setLoadingInfo(prev => ({...prev, [type]: true}));
    try {
      const token = localStorage.getItem('master_token');
      const response = await axios.post(`${API}/master/tenant-info`, { email: email.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (type === 'source') setSourceInfo(response.data);
      else setTargetInfo(response.data);
    } catch (error) {
      if (type === 'source') setSourceInfo(null);
      else setTargetInfo(null);
      toast.error(error.response?.data?.detail || `Usuário não encontrado: ${email}`);
    } finally {
      setLoadingInfo(prev => ({...prev, [type]: false}));
    }
  };

  const handleRepairData = async () => {
    if (!repairForm.source_email || !repairForm.target_email) {
      toast.error('Preencha os emails de origem e destino');
      return;
    }
    setRepairLoading(true);
    setRepairResult(null);
    try {
      const token = localStorage.getItem('master_token');
      const payload = {
        source_email: repairForm.source_email,
        target_email: repairForm.target_email,
        dry_run: repairForm.dry_run
      };
      if (repairForm.target_calendar_id_override.trim()) {
        payload.target_calendar_id_override = repairForm.target_calendar_id_override.trim();
      }
      const response = await axios.post(`${API}/master/repair-tenant-data`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRepairResult(response.data);
      if (repairForm.dry_run) {
        toast.info('Simulação concluída. Revise os dados antes de executar.');
      } else {
        toast.success(`Reparo executado: ${response.data.consultas_transferred} consultas transferidas`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao executar reparo';
      toast.error(msg);
      setRepairResult({ error: msg });
    } finally {
      setRepairLoading(false);
    }
  };


  const handleNovoCliente = async (e) => {
    e.preventDefault();
    
    if (formData.login_senha.length !== 6) {
      toast.error('A senha deve ter exatamente 6 caracteres');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('master_token');
      await axios.post(`${API}/master/clientes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cliente criado com sucesso!');
      setShowNovoCliente(false);
      setFormData({
        nome_clinica: '',
        cnpj: '',
        telefone: '',
        email_contato: '',
        site: '',
        endereco: '',
        observacoes: '',
        login_email: '',
        login_senha: ''
      });
      fetchClientes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCliente = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('master_token');
      await axios.put(`${API}/master/clientes/${showEditCliente.id}`, {
        nome_clinica: formData.nome_clinica,
        cnpj: formData.cnpj,
        telefone: formData.telefone,
        email_contato: formData.email_contato,
        site: formData.site,
        endereco: formData.endereco,
        observacoes: formData.observacoes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cliente atualizado com sucesso!');
      setShowEditCliente(null);
      fetchClientes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar cliente');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditLogin = async (e) => {
    e.preventDefault();
    
    if (loginUpdateData.nova_senha && loginUpdateData.nova_senha.length !== 6) {
      toast.error('A senha deve ter exatamente 6 caracteres');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('master_token');
      const updateData = {};
      if (loginUpdateData.novo_email) updateData.novo_email = loginUpdateData.novo_email;
      if (loginUpdateData.nova_senha) updateData.nova_senha = loginUpdateData.nova_senha;
      
      if (Object.keys(updateData).length === 0) {
        toast.error('Informe ao menos um campo para atualizar');
        setSubmitting(false);
        return;
      }
      
      await axios.put(`${API}/master/clientes/${showEditLogin.id}/login`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dados de login atualizados com sucesso!');
      setShowEditLogin(null);
      setLoginUpdateData({ novo_email: '', nova_senha: '' });
      fetchClientes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar login');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (clienteId, novoStatus) => {
    try {
      const token = localStorage.getItem('master_token');
      await axios.put(`${API}/master/clientes/${clienteId}/status?novo_status=${novoStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const statusMsg = {
        ativo: 'Acesso liberado com sucesso!',
        bloqueado: 'Acesso bloqueado com sucesso!',
        cancelado: 'Acesso cancelado com sucesso!'
      };
      toast.success(statusMsg[novoStatus]);
      fetchClientes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const openEditModal = (cliente) => {
    setFormData({
      nome_clinica: cliente.nome_clinica || '',
      cnpj: cliente.cnpj || '',
      telefone: cliente.telefone || '',
      email_contato: cliente.email_contato || '',
      site: cliente.site || '',
      endereco: cliente.endereco || '',
      observacoes: cliente.observacoes || '',
      login_email: '',
      login_senha: ''
    });
    setShowEditCliente(cliente);
  };

  const getStatusBadge = (status) => {
    const styles = {
      ativo: 'bg-green-100 text-green-800',
      bloqueado: 'bg-yellow-100 text-yellow-800',
      cancelado: 'bg-red-100 text-red-800'
    };
    const labels = {
      ativo: 'Ativo',
      bloqueado: 'Bloqueado',
      cancelado: 'Cancelado'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-800">Painel Master</CardTitle>
            <CardDescription>Acesso restrito ao administrador</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  required
                  data-testid="master-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  value={loginForm.senha}
                  onChange={(e) => setLoginForm({...loginForm, senha: e.target.value})}
                  required
                  data-testid="master-login-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="master-login-submit">
                {submitting ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-slate-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Painel Master</h1>
            <p className="text-sm text-slate-300">Gerenciamento de Clientes</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="text-white border-white hover:bg-slate-700">
            Sair
          </Button>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 pb-0 flex gap-0">
          <button
            onClick={() => setActiveTab('clientes')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'clientes'
                ? 'border-white text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid="tab-clientes"
          >
            Clientes
          </button>
          <button
            onClick={() => setActiveTab('configuracao')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'configuracao'
                ? 'border-white text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid="tab-configuracao-global"
          >
            Configuração Global
          </button>
          <button
            onClick={() => { setActiveTab('ferramentas'); fetchAuditLogs(); }}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'ferramentas'
                ? 'border-white text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid="tab-ferramentas"
          >
            Ferramentas
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'backup'
                ? 'border-white text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid="tab-backup"
          >
            Backup
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">

        {/* Tab: Configuração Global */}
        {activeTab === 'configuracao' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Configuração Global</h2>

            {/* Google Calendar Setup */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Integração Google Calendar</CardTitle>
                <CardDescription>Passo a passo para conectar o Google Calendar ao app de cada cliente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-700">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-800 mb-2">URI de Redirecionamento (Callback)</p>
                  <code className="block bg-white border border-blue-100 rounded px-3 py-2 text-blue-900 text-xs break-all select-all">
                    https://app.agendamaismed.com.br/api/auth/google/callback
                  </code>
                </div>

                <div className="space-y-3">
                  <p className="font-semibold text-slate-800 text-base">Passo a passo - Google Cloud Console</p>
                  
                  <div className="space-y-2 pl-1">
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">1</span>
                      <p>Acesse <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-medium">console.cloud.google.com</a></p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">2</span>
                      <p>Navegue até <strong>APIs & Services → Credentials</strong></p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">3</span>
                      <p>Clique no <strong>OAuth 2.0 Client ID</strong> do projeto</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">4</span>
                      <div>
                        <p>Em <strong>Authorized redirect URIs</strong>, adicione:</p>
                        <code className="block mt-1 bg-gray-100 rounded px-2 py-1 text-xs break-all">https://app.agendamaismed.com.br/api/auth/google/callback</code>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">5</span>
                      <p>Clique em <strong>Salvar</strong></p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="font-semibold text-slate-800 text-base">Passo a passo - No App do Cliente</p>
                  
                  <div className="space-y-2 pl-1">
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">1</span>
                      <p>Acesse o app como master: <strong>master_EMAIL_DO_CLIENTE</strong> e senha master</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">2</span>
                      <p>Vá em <strong>Configurações → Google Calendar</strong></p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">3</span>
                      <p>Insira o <strong>Client ID</strong> e <strong>Client Secret</strong> do Google Cloud</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">4</span>
                      <p>Clique em <strong>Conectar Google Calendar</strong> e autorize o acesso</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">5</span>
                      <p>Selecione o <strong>calendário</strong> desejado para sincronização</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-amber-800 mb-1">Informações Importantes</p>
                  <ul className="list-disc pl-4 space-y-1 text-amber-900 text-xs">
                    <li>A URI de redirecionamento <strong>deve</strong> estar cadastrada no Google Cloud antes de conectar</li>
                    <li>A sincronização é bidirecional: eventos criados no app aparecem no Calendar e vice-versa</li>
                    <li>Cada cliente precisa ter seu próprio Client ID e Client Secret configurado</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Credenciais Master */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Credenciais de Acesso Master</CardTitle>
                <CardDescription>Informações de acesso ao painel e aos apps dos clientes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <p className="font-semibold text-slate-800 mb-2">Acesso ao Painel Master</p>
                    <p><strong>URL:</strong> <code className="text-xs bg-gray-200 px-1 rounded">/painel/master</code></p>
                    <p><strong>Login:</strong> Email e senha do administrador master</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <p className="font-semibold text-slate-800 mb-2">Acesso ao App do Cliente</p>
                    <p><strong>Email:</strong> <code className="text-xs bg-gray-200 px-1 rounded">master_</code> + email do cliente</p>
                    <p><strong>Senha:</strong> Senha de acesso master (não a senha do cliente)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab: Ferramentas */}
        {activeTab === 'ferramentas' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Ferramentas</h2>

            {/* Transferencia Direta - PRINCIPAL */}
            <Card className="border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg">Transferência Direta de Consultas</CardTitle>
                <CardDescription>
                  Selecione as consultas que deseja transferir de um cliente para outro. Você tem controle total sobre quais consultas mover.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-semibold">Origem — quem TEM os dados errados</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="transfer-source"
                        placeholder="Email de login no app"
                        value={transferSource}
                        onChange={e => { setTransferSource(e.target.value); setTransferConsultas([]); setTransferSourceInfo(null); }}
                      />
                      <Button variant="outline" size="sm" disabled={transferLoading} onClick={() => loadTransferConsultas(transferSource)}>
                        {transferLoading ? '...' : 'Carregar'}
                      </Button>
                    </div>
                    {transferSourceInfo && (
                      <p className="text-xs text-gray-500">
                        {transferSourceInfo.nome} — {transferSourceInfo.total} consultas
                        {transferSourceInfo.google_connected ? <span className="ml-1 text-blue-600">(Google conectado)</span> : <span className="ml-1 text-gray-400">(sem Google)</span>}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold">Destino — quem DEVERIA ter os dados</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="transfer-target"
                        placeholder="Email de login no app"
                        value={transferTarget}
                        onChange={e => { setTransferTarget(e.target.value); setTransferTargetInfo(null); }}
                      />
                      <Button variant="outline" size="sm" onClick={() => loadTransferTargetInfo(transferTarget)}>
                        Verificar
                      </Button>
                    </div>
                    {transferTargetInfo && (
                      <p className="text-xs text-gray-500">
                        {transferTargetInfo.nome} — {transferTargetInfo.total_consultas} consultas atualmente
                        {transferTargetInfo.google_connected ? <span className="ml-1 text-blue-600">(Google conectado)</span> : <span className="ml-1 text-gray-400">(sem Google)</span>}
                      </p>
                    )}
                  </div>
                </div>

                {transferConsultas.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{transferConsultas.length} consultas encontradas — selecione as que deseja transferir:</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={toggleSelectAll}>
                          {transferSelected.size === transferConsultas.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                        </Button>
                        <Badge variant="outline">{transferSelected.size} selecionadas</Badge>
                      </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="p-2 text-left w-8">
                              <input type="checkbox" checked={transferSelected.size === transferConsultas.length && transferConsultas.length > 0} onChange={toggleSelectAll} />
                            </th>
                            <th className="p-2 text-left">Paciente</th>
                            <th className="p-2 text-left">Data</th>
                            <th className="p-2 text-left">Horário</th>
                            <th className="p-2 text-left">Status</th>
                            <th className="p-2 text-left">Tipo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transferConsultas.map(c => (
                            <tr key={c.id} className={`border-t cursor-pointer hover:bg-blue-50 ${transferSelected.has(c.id) ? 'bg-blue-50' : ''}`}
                                onClick={() => setTransferSelected(prev => {
                                  const next = new Set(prev);
                                  next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                                  return next;
                                })}>
                              <td className="p-2">
                                <input type="checkbox" checked={transferSelected.has(c.id)} readOnly />
                              </td>
                              <td className="p-2 font-medium">{c.paciente_nome}</td>
                              <td className="p-2">{c.data_consulta}</td>
                              <td className="p-2">{c.hora_inicio} - {c.hora_fim}</td>
                              <td className="p-2">{c.status}</td>
                              <td className="p-2 text-xs">{c.categoria || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      <Button
                        data-testid="btn-execute-transfer"
                        variant="destructive"
                        disabled={transferLoading || transferSelected.size === 0 || !transferTarget}
                        onClick={executeTransfer}
                      >
                        {transferLoading ? 'Transferindo...' : `Transferir ${transferSelected.size} consultas para ${transferTarget || '...'}`}
                      </Button>
                      <Button
                        data-testid="btn-cleanup-pacientes"
                        variant="outline"
                        disabled={cleanupLoading || !transferSource || !transferTarget}
                        onClick={cleanupDuplicatePacientes}
                      >
                        {cleanupLoading ? 'Limpando...' : 'Limpar pacientes duplicados da origem'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Diagnóstico de Sincronização */}
            <Card className="border-2 border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg">Diagnóstico de Sincronização</CardTitle>
                <CardDescription>
                  Compara os eventos do Google Calendar de um cliente com as consultas no app e identifica por que algum evento pode não estar aparecendo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    data-testid="sync-diag-email"
                    placeholder="Email de login do cliente no app"
                    value={syncDiagEmail}
                    onChange={e => { setSyncDiagEmail(e.target.value); setSyncDiagResult(null); }}
                    className="flex-1"
                  />
                  <Button
                    data-testid="btn-sync-diag"
                    onClick={runSyncDiagnostico}
                    disabled={syncDiagLoading}
                    variant="outline"
                  >
                    {syncDiagLoading ? 'Analisando...' : 'Analisar'}
                  </Button>
                </div>

                {syncDiagResult && (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-slate-800">{syncDiagResult.total_eventos_google}</p>
                        <p className="text-xs text-slate-500">Eventos no Calendar</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-700">{syncDiagResult.resumo?.sincronizado || 0}</p>
                        <p className="text-xs text-green-600">Sincronizados</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-700">
                          {(syncDiagResult.resumo?.bloqueado_cancelada || 0) + (syncDiagResult.resumo?.conflito || 0)}
                        </p>
                        <p className="text-xs text-red-600">Com Problema</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-700">{syncDiagResult.resumo?.pendente || 0}</p>
                        <p className="text-xs text-blue-600">Pendentes</p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500">Calendário: {syncDiagResult.calendario} | {syncDiagResult.total_consultas_app} consultas no app</p>

                    {/* Filter */}
                    <div className="flex gap-1 flex-wrap">
                      {[
                        { key: 'todos', label: 'Todos', count: syncDiagResult.eventos?.length || 0 },
                        { key: 'bloqueado_cancelada', label: 'Bloqueado (Cancelada)', count: syncDiagResult.resumo?.bloqueado_cancelada || 0, color: 'text-red-700' },
                        { key: 'conflito', label: 'Conflito', count: syncDiagResult.resumo?.conflito || 0, color: 'text-amber-700' },
                        { key: 'pendente', label: 'Pendente', count: syncDiagResult.resumo?.pendente || 0, color: 'text-blue-700' },
                        { key: 'sincronizado', label: 'Sincronizado', count: syncDiagResult.resumo?.sincronizado || 0, color: 'text-green-700' },
                        { key: 'ignorado', label: 'Ignorado', count: syncDiagResult.resumo?.ignorado || 0, color: 'text-gray-500' },
                      ].filter(f => f.count > 0 || f.key === 'todos').map(f => (
                        <button
                          key={f.key}
                          onClick={() => setSyncDiagFilter(f.key)}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                            syncDiagFilter === f.key
                              ? 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {f.label} ({f.count})
                        </button>
                      ))}
                    </div>

                    {/* Fix all button */}
                    {(() => {
                      const fixableEvents = (syncDiagResult.eventos || []).filter(ev =>
                        ['bloqueado_cancelada', 'conflito', 'pendente', 'calendario_diferente'].includes(ev.status_sync)
                      );
                      return fixableEvents.length > 0 ? (
                        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <span className="text-sm text-amber-900 font-medium flex-1">
                            {fixableEvents.length} evento(s) com problema podem ser corrigidos
                          </span>
                          <Button
                            data-testid="btn-fix-all-sync"
                            size="sm"
                            variant="destructive"
                            disabled={syncFixLoading}
                            onClick={() => handleForcarSync(fixableEvents.map(ev => ev.google_event_id))}
                          >
                            {syncFixLoading ? 'Corrigindo...' : `Corrigir Todos (${fixableEvents.length})`}
                          </Button>
                        </div>
                      ) : null;
                    })()}

                    {/* Events table */}
                    <div className="max-h-96 overflow-y-auto border rounded-lg">
                      <table className="w-full text-sm" data-testid="sync-diag-table">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Evento Google Calendar</th>
                            <th className="p-2 text-left">Data</th>
                            <th className="p-2 text-left">Horário</th>
                            <th className="p-2 text-left">Status</th>
                            <th className="p-2 text-left">Diagnóstico</th>
                            <th className="p-2 text-left w-24">Ação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(syncDiagResult.eventos || [])
                            .filter(ev => syncDiagFilter === 'todos' || ev.status_sync === syncDiagFilter)
                            .map((ev, i) => (
                            <tr key={i} className={`border-t ${
                              ev.status_sync === 'sincronizado' ? '' :
                              ev.status_sync === 'bloqueado_cancelada' ? 'bg-red-50' :
                              ev.status_sync === 'conflito' ? 'bg-amber-50' :
                              ev.status_sync === 'pendente' ? 'bg-blue-50' :
                              ev.status_sync === 'ignorado' ? 'bg-gray-50' : ''
                            }`}>
                              <td className="p-2 font-medium max-w-[200px] truncate">{ev.evento_google}</td>
                              <td className="p-2 whitespace-nowrap">{ev.data}</td>
                              <td className="p-2 whitespace-nowrap">{ev.horario}</td>
                              <td className="p-2">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  ev.status_sync === 'sincronizado' ? 'bg-green-100 text-green-800' :
                                  ev.status_sync === 'bloqueado_cancelada' ? 'bg-red-100 text-red-800' :
                                  ev.status_sync === 'conflito' ? 'bg-amber-100 text-amber-800' :
                                  ev.status_sync === 'pendente' ? 'bg-blue-100 text-blue-800' :
                                  ev.status_sync === 'similar_encontrada' ? 'bg-yellow-100 text-yellow-800' :
                                  ev.status_sync === 'calendario_diferente' ? 'bg-amber-100 text-amber-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {ev.status_sync === 'sincronizado' ? 'Sincronizado' :
                                   ev.status_sync === 'bloqueado_cancelada' ? 'Bloqueado' :
                                   ev.status_sync === 'conflito' ? 'Conflito' :
                                   ev.status_sync === 'pendente' ? 'Pendente' :
                                   ev.status_sync === 'similar_encontrada' ? 'Similar' :
                                   ev.status_sync === 'calendario_diferente' ? 'Calendário' :
                                   'Ignorado'}
                                </span>
                              </td>
                              <td className="p-2 text-xs text-gray-600 max-w-[250px]">
                                {ev.motivo}
                                {ev.consulta_app_status && (
                                  <span className="ml-1 text-gray-400">[App: {ev.consulta_app_status}]</span>
                                )}
                              </td>
                              <td className="p-2">
                                {['bloqueado_cancelada', 'conflito', 'pendente', 'calendario_diferente'].includes(ev.status_sync) && (
                                  <button
                                    data-testid={`btn-fix-sync-${i}`}
                                    disabled={syncFixingIds.has(ev.google_event_id) || syncFixLoading}
                                    onClick={() => handleForcarSync([ev.google_event_id])}
                                    className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 whitespace-nowrap"
                                  >
                                    {syncFixingIds.has(ev.google_event_id) ? '...' : 'Forçar Sync'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Log de Auditoria */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Log de Auditoria</CardTitle>
                    <CardDescription>
                      Histórico de todas as transferências e reparos realizados.
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" data-testid="btn-refresh-audit" onClick={fetchAuditLogs} disabled={auditLoading}>
                    {auditLoading ? 'Carregando...' : 'Atualizar'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum registro de auditoria encontrado.</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {auditLogs.map((log) => (
                      <div key={log.id} data-testid={`audit-log-${log.id}`} className="border rounded-lg p-3 text-sm space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant={log.action === 'transfer_consultas' ? 'default' : 'secondary'}>
                              {log.action === 'transfer_consultas' ? 'Transferência' : 'Reparo Automático'}
                            </Badge>
                            <span className="font-medium">{log.consultas_transferred} consultas, {log.pacientes_transferred} pacientes</span>
                          </div>
                          <span className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString('pt-BR')}</span>
                        </div>
                        {log.action === 'transfer_consultas' && (
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">De:</span> {log.source_email} → <span className="font-medium">Para:</span> {log.target_email}
                          </p>
                        )}
                        <p className="text-xs text-gray-400">Por: {log.performed_by}</p>
                        {(log.google_deleted > 0 || log.google_created > 0) && (
                          <p className="text-xs text-blue-600">
                            Google Calendar: {log.google_deleted || 0} removidos da origem, {log.google_created || 0} criados no destino
                          </p>
                        )}
                        {log.google_errors?.length > 0 && (
                          <p className="text-xs text-red-500">Erros Google: {log.google_errors.join('; ')}</p>
                        )}
                        {log.details && log.details.length > 0 && (
                          <div className="mt-2 border rounded overflow-hidden">
                            <p className="text-xs font-medium bg-gray-50 px-2 py-1 border-b">Consultas transferidas:</p>
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-2 py-1 text-left">Paciente</th>
                                  <th className="px-2 py-1 text-left">Data</th>
                                  <th className="px-2 py-1 text-left">Horário</th>
                                  <th className="px-2 py-1 text-left">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {log.details.map((d, i) => {
                                  const parts = d.split(' | ');
                                  return (
                                    <tr key={i} className="border-t">
                                      <td className="px-2 py-1 font-medium">{parts[0] || '—'}</td>
                                      <td className="px-2 py-1">{parts[1] || '—'}</td>
                                      <td className="px-2 py-1">{parts[2] || '—'}</td>
                                      <td className="px-2 py-1">{parts[3] || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Diagnostico */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Migração de Arquivos para GridFS</CardTitle>
                <CardDescription>
                  Copia arquivos do disco local para o MongoDB (GridFS), garantindo que sobrevivam a futuros deploys.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  data-testid="btn-migrate-gridfs"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('master_token');
                      const response = await axios.post(`${API}/master/migrate-files-to-gridfs`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(response.data.message);
                      if (response.data.errors?.length > 0) {
                        toast.warning(`Erros: ${response.data.errors.slice(0, 3).join('; ')}`);
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erro na migração');
                    }
                  }}
                >
                  Migrar Arquivos para GridFS
                </Button>
              </CardContent>
            </Card>

            {/* Diagnostico de Isolamento */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Diagnóstico de Isolamento</CardTitle>
                <CardDescription>
                  Escaneia todos os clientes e verifica se há dados de um cliente aparecendo em outro.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  data-testid="btn-diagnostico"
                  onClick={runDiagnostico}
                  disabled={diagnosticoLoading}
                >
                  {diagnosticoLoading ? 'Escaneando...' : 'Escanear Todos os Clientes'}
                </Button>

                {diagnostico && (
                  <div className="space-y-3">
                    <div className={`rounded-lg p-3 border text-sm font-medium ${diagnostico.total_issues === 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                      {diagnostico.total_issues === 0
                        ? `Tudo certo! ${diagnostico.total_clientes} clientes verificados, nenhum problema encontrado.`
                        : `Problemas encontrados em ${diagnostico.total_clientes} clientes. Clique em "Simular Reparo de Todos" para ver o que pode ser corrigido.`
                      }
                    </div>

                    {diagnostico.total_issues > 0 && (
                      <div className="flex gap-3">
                        <Button
                          data-testid="btn-repair-all-simulate"
                          variant="outline"
                          disabled={repairAllLoading}
                          onClick={() => handleRepairAll(true)}
                        >
                          {repairAllLoading ? 'Processando...' : 'Simular Reparo de Todos'}
                        </Button>
                        <Button
                          data-testid="btn-repair-all-execute"
                          variant="destructive"
                          disabled={repairAllLoading}
                          onClick={() => {
                            if (window.confirm('Tem certeza? Isso vai corrigir os dados de TODOS os clientes contaminados.')) {
                              handleRepairAll(false);
                            }
                          }}
                        >
                          {repairAllLoading ? 'Reparando...' : 'Reparar Todos Automaticamente'}
                        </Button>
                      </div>
                    )}

                    {repairAllResult && (
                      <div className={`rounded-lg p-4 border text-sm ${repairAllResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                        <p className="font-semibold mb-2">{repairAllResult.summary}</p>
                        {repairAllResult.detalhes?.map((cli, i) => (
                          <div key={i} className="mb-3 pl-3 border-l-2 border-gray-300">
                            <p className="font-medium text-xs">{cli.nome || cli.email}</p>
                            {cli.transfers.map((t, j) => (
                              <div key={j} className="pl-2 mt-1">
                                <p className="text-xs text-gray-600">
                                  {t.count} consultas → <strong>{t.target}</strong>
                                  <span className="text-gray-400 ml-1">({t.reason})</span>
                                </p>
                                {t.consultas && t.consultas.length > 0 && (
                                  <ul className="mt-1 text-xs text-gray-500 max-h-32 overflow-y-auto">
                                    {t.consultas.map((c, k) => (
                                      <li key={k} className="pl-2 border-l border-gray-200">{c}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      {diagnostico.clientes.map((c, i) => (
                        <div key={i} className={`rounded-lg p-3 border text-sm ${c.status === 'ok' ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <strong>{c.nome || c.email}</strong>
                              <span className="text-gray-500 ml-2 text-xs">{c.email}</span>
                              {c.google_connected && <span className="text-xs ml-2 text-blue-600">(Google conectado)</span>}
                              {!c.google_connected && <span className="text-xs ml-2 text-gray-400">(sem Google)</span>}
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <span>{c.total_consultas} consultas</span>
                              <span>{c.total_pacientes} pacientes</span>
                              {c.status === 'ok' ? (
                                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">OK</Badge>
                              ) : c.status === 'suspeito' ? (
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">Suspeito</Badge>
                              ) : (
                                <Badge variant="destructive">Contaminado</Badge>
                              )}
                            </div>
                          </div>
                          {c.issues && c.issues.length > 0 && (
                            <div className="mt-2 pl-3 border-l-2 border-red-300 text-xs text-red-800">
                              {c.issues.map((issue, j) => (
                                <p key={j}>{issue.description}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reparar Dados */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Reparar Dados entre Clientes</CardTitle>
                <CardDescription>
                  Transfere consultas e pacientes que foram atribuídos ao cliente errado de volta para o dono correto.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <strong>Como funciona:</strong> Digite os emails de <strong>login do app</strong> (não o Gmail), clique "Buscar" para confirmar, 
                  e depois "Simular". O sistema detecta automaticamente quais consultas precisam ser transferidas.
                </div>

                {/* Source */}
                <div className="space-y-2">
                  <Label className="font-semibold">Origem — quem TEM os dados errados</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="repair-source-email"
                      placeholder="Email de login no app (ex: bruna@agendamaismed.com.br)"
                      value={repairForm.source_email}
                      onChange={e => { setRepairForm(f => ({...f, source_email: e.target.value})); setSourceInfo(null); }}
                    />
                    <Button variant="outline" size="sm" disabled={loadingInfo.source} onClick={() => fetchTenantInfo(repairForm.source_email, 'source')}>
                      {loadingInfo.source ? '...' : 'Buscar'}
                    </Button>
                  </div>
                  {sourceInfo && (
                    <div className="bg-gray-50 border rounded-lg p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div><span className="text-gray-500">Nome:</span> <strong>{sourceInfo.nome || '—'}</strong></div>
                      <div><span className="text-gray-500">Consultas:</span> <strong>{sourceInfo.total_consultas}</strong></div>
                      <div><span className="text-gray-500">Pacientes:</span> <strong>{sourceInfo.total_pacientes}</strong></div>
                      <div><span className="text-gray-500">Google:</span> <strong>{sourceInfo.google_connected ? sourceInfo.google_email || 'Conectado' : 'Não conectado'}</strong></div>
                    </div>
                  )}
                </div>

                {/* Target */}
                <div className="space-y-2">
                  <Label className="font-semibold">Destino — quem DEVERIA ter os dados</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="repair-target-email"
                      placeholder="Email de login no app (ex: admin@agendamaismed.com)"
                      value={repairForm.target_email}
                      onChange={e => { setRepairForm(f => ({...f, target_email: e.target.value})); setTargetInfo(null); }}
                    />
                    <Button variant="outline" size="sm" disabled={loadingInfo.target} onClick={() => fetchTenantInfo(repairForm.target_email, 'target')}>
                      {loadingInfo.target ? '...' : 'Buscar'}
                    </Button>
                  </div>
                  {targetInfo && (
                    <div className="bg-gray-50 border rounded-lg p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div><span className="text-gray-500">Nome:</span> <strong>{targetInfo.nome || '—'}</strong></div>
                      <div><span className="text-gray-500">Consultas:</span> <strong>{targetInfo.total_consultas}</strong></div>
                      <div><span className="text-gray-500">Pacientes:</span> <strong>{targetInfo.total_pacientes}</strong></div>
                      <div><span className="text-gray-500">Google:</span> <strong>{targetInfo.google_connected ? targetInfo.google_email || 'Conectado' : 'Não conectado'}</strong></div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    data-testid="repair-dry-run-btn"
                    variant="outline"
                    disabled={repairLoading || !sourceInfo || !targetInfo}
                    onClick={() => { repairForm.dry_run = true; handleRepairData(); }}
                  >
                    {repairLoading && repairForm.dry_run ? 'Simulando...' : 'Simular (ver antes de executar)'}
                  </Button>
                  <Button
                    data-testid="repair-execute-btn"
                    variant="destructive"
                    disabled={repairLoading || !sourceInfo || !targetInfo}
                    onClick={() => {
                      if (window.confirm('Tem certeza? Esta ação vai transferir consultas e pacientes entre clientes.')) {
                        repairForm.dry_run = false;
                        handleRepairData();
                      }
                    }}
                  >
                    {repairLoading && !repairForm.dry_run ? 'Executando...' : 'Executar Reparo'}
                  </Button>
                </div>

                {repairResult && (
                  <div className={`rounded-lg p-4 border text-sm ${repairResult.error ? 'bg-red-50 border-red-200' : repairResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    {repairResult.error ? (
                      <p className="text-red-800 font-medium">{repairResult.error}</p>
                    ) : (
                      <>
                        <p className="font-semibold mb-1">{repairResult.summary}</p>
                        {repairResult.detection_method && (
                          <p className="text-xs text-gray-600 mb-2">{repairResult.detection_method}</p>
                        )}
                        {repairResult.details && repairResult.details.length > 0 ? (
                          <ul className="space-y-1 text-xs max-h-48 overflow-y-auto">
                            {repairResult.details.map((d, i) => (
                              <li key={i} className="pl-2 border-l-2 border-current">{d}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-600">Nenhuma consulta suspeita encontrada. Os dados já estão corretos.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab: Backup */}
        {activeTab === 'backup' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Backup & Restauração</h2>
            <p className="text-sm text-slate-600 -mt-4">
              Exporte todas as coleções críticas em um ZIP ou importe um backup anterior. Sempre baixe um backup antes de restaurar.
            </p>

            {/* Export */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Exportar Backup Completo</CardTitle>
                <CardDescription>
                  Gera um ZIP com JSONs de todas as coleções críticas: consultas, pacientes, pagamentos, prontuários, exames, configs etc.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={backupIncludeFiles}
                    onChange={(e) => setBackupIncludeFiles(e.target.checked)}
                    data-testid="checkbox-include-files"
                  />
                  <span className="text-sm text-slate-700">
                    Incluir arquivos de prontuário (anexos médicos em base64)
                  </span>
                </label>
                <Button
                  onClick={handleBackupDownload}
                  disabled={backupLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="btn-baixar-backup"
                >
                  {backupLoading ? 'Gerando...' : 'Baixar Backup (.zip)'}
                </Button>
                <p className="text-xs text-slate-500 mt-3">
                  Recomendado: guarde o arquivo em um local seguro (Google Drive, Dropbox ou disco externo).
                </p>
              </CardContent>
            </Card>

            {/* Restore */}
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>Restaurar Backup</span>
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">Operação sensível</Badge>
                </CardTitle>
                <CardDescription>
                  Envie um ZIP gerado anteriormente. Use o Preview para conferir as mudanças antes de executar a restauração.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Arquivo de backup (.zip)</Label>
                  <Input
                    type="file"
                    accept=".zip"
                    onChange={(e) => { setRestoreFile(e.target.files?.[0] || null); setPreviewResult(null); setRestoreResult(null); }}
                    data-testid="input-restore-file"
                  />
                  {restoreFile && <p className="text-xs text-slate-500 mt-1">Selecionado: {restoreFile.name} ({Math.round(restoreFile.size/1024)} KB)</p>}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handlePreviewRestore}
                    disabled={!restoreFile || previewLoading}
                    variant="outline"
                    data-testid="btn-preview-restore"
                  >
                    {previewLoading ? 'Analisando...' : '1. Visualizar Diferenças'}
                  </Button>
                </div>

                {previewResult && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-semibold text-slate-800">Diff por coleção</p>
                      <Badge className="bg-slate-200 text-slate-700">
                        Backup gerado em {new Date(previewResult.manifest?.generated_at).toLocaleString('pt-BR')}
                      </Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 text-left">
                            <th className="py-1.5 px-2">Coleção</th>
                            <th className="py-1.5 px-2 text-right">Backup</th>
                            <th className="py-1.5 px-2 text-right">Atual</th>
                            <th className="py-1.5 px-2 text-right text-green-700">Inserir</th>
                            <th className="py-1.5 px-2 text-right text-blue-700">Atualizar</th>
                            <th className="py-1.5 px-2 text-right text-red-700">Remover*</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(previewResult.diff || {}).map(([name, d]) => (
                            <tr key={name} className="border-b border-slate-100">
                              <td className="py-1.5 px-2 font-medium">{name}</td>
                              <td className="py-1.5 px-2 text-right">{d.backup_count}</td>
                              <td className="py-1.5 px-2 text-right">{d.current_count}</td>
                              <td className="py-1.5 px-2 text-right text-green-700">{d.would_insert}</td>
                              <td className="py-1.5 px-2 text-right text-blue-700">{d.would_update}</td>
                              <td className="py-1.5 px-2 text-right text-red-700">{d.would_remove}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">
                      * Documentos que existem hoje no banco mas NÃO estão no backup. Só são de fato removidos no modo <strong>replace</strong>.
                    </p>
                  </div>
                )}

                <div className="border-t pt-4 space-y-3">
                  <div>
                    <Label>Modo de restauração</Label>
                    <div className="flex gap-3 mt-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="merge"
                          checked={restoreMode === 'merge'}
                          onChange={() => setRestoreMode('merge')}
                          data-testid="radio-mode-merge"
                        />
                        <div>
                          <p className="text-sm font-medium">Merge (recomendado)</p>
                          <p className="text-xs text-slate-500">Upsert por ID. Não apaga dados. Seguro.</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="replace"
                          checked={restoreMode === 'replace'}
                          onChange={() => setRestoreMode('replace')}
                          data-testid="radio-mode-replace"
                        />
                        <div>
                          <p className="text-sm font-medium text-red-700">Replace (destrutivo)</p>
                          <p className="text-xs text-red-600">Apaga tudo antes de restaurar.</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label>Para confirmar, digite <strong>RESTAURAR</strong></Label>
                    <Input
                      type="text"
                      value={restoreConfirmText}
                      onChange={(e) => setRestoreConfirmText(e.target.value)}
                      placeholder="RESTAURAR"
                      data-testid="input-confirm-restore"
                    />
                  </div>

                  <Button
                    onClick={handleExecuteRestore}
                    disabled={!restoreFile || restoreConfirmText !== 'RESTAURAR' || restoreLoading}
                    className={`${restoreMode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                    data-testid="btn-execute-restore"
                  >
                    {restoreLoading
                      ? 'Restaurando...'
                      : `2. Executar Restauração (${restoreMode === 'merge' ? 'Merge' : 'Replace'})`}
                  </Button>
                </div>

                {restoreResult && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="font-semibold text-green-900 mb-2">
                      ✅ {restoreResult.message}
                    </p>
                    <p className="text-sm text-green-800">
                      Modo: <strong>{restoreResult.mode}</strong> · Inseridos: {restoreResult.total_inserted} · Atualizados: {restoreResult.total_updated} · Removidos: {restoreResult.total_removed}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Backfill Retornos */}
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>Backfill de Retornos</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">Pós-deploy</Badge>
                </CardTitle>
                <CardDescription>
                  Vincula automaticamente consultas existentes (criadas antes do auto-link estar ativo) que deveriam ser retornos. Use o Dry-run primeiro para revisar o que será alterado.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => handleBackfillRetornos(false)}
                    disabled={backfillLoading}
                    variant="outline"
                    data-testid="btn-backfill-dry"
                  >
                    {backfillLoading ? 'Analisando...' : '1. Visualizar (Dry-run)'}
                  </Button>
                  <Button
                    onClick={() => handleBackfillRetornos(true)}
                    disabled={backfillLoading || !backfillResult}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-backfill-apply"
                  >
                    {backfillLoading ? 'Aplicando...' : '2. Aplicar Alterações'}
                  </Button>
                </div>

                {backfillResult && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">
                    <p className="font-semibold text-slate-800 mb-2">
                      {backfillResult.dry_run ? 'Preview (nada foi alterado)' : 'Aplicado!'}
                    </p>
                    <p className="text-slate-700">
                      Origens processadas: <strong>{backfillResult.origens_processadas}</strong> ·
                      Origens com mudança: <strong>{backfillResult.total_origens_atualizadas}</strong> ·
                      Consultas vinculadas como retorno: <strong>{backfillResult.total_consultas_vinculadas}</strong>
                    </p>
                    {backfillResult.consultas_vinculadas?.length > 0 && (
                      <div className="mt-3 max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="text-left border-b border-slate-200">
                            <tr>
                              <th className="py-1.5 px-2">Paciente</th>
                              <th className="py-1.5 px-2">Procedimento</th>
                              <th className="py-1.5 px-2">Data</th>
                              <th className="py-1.5 px-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {backfillResult.consultas_vinculadas.map((c, i) => (
                              <tr key={i} className="border-b border-slate-100">
                                <td className="py-1.5 px-2">{c.paciente_nome}</td>
                                <td className="py-1.5 px-2">{c.categoria}</td>
                                <td className="py-1.5 px-2">{c.data_consulta} {c.hora_inicio}</td>
                                <td className="py-1.5 px-2">{c.status_atual}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab: Clientes */}
        {activeTab === 'clientes' && (
        <>
        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-800">Clientes ({clientes.length})</h2>
          <Button onClick={() => setShowNovoCliente(true)} data-testid="btn-novo-cliente">
            + Novo Cliente
          </Button>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clientes.map((cliente) => (
            <Card key={cliente.id} className="hover:shadow-lg transition-shadow" data-testid={`cliente-card-${cliente.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{cliente.nome_clinica}</CardTitle>
                  {getStatusBadge(cliente.status)}
                </div>
                <CardDescription>{cliente.login_email}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {cliente.cnpj && <p><strong>CNPJ:</strong> {cliente.cnpj}</p>}
                  {cliente.telefone && <p><strong>Tel:</strong> {cliente.telefone}</p>}
                  {cliente.email_contato && <p><strong>Email:</strong> {cliente.email_contato}</p>}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEditModal(cliente)}>
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    setLoginUpdateData({ novo_email: '', nova_senha: '' });
                    setShowEditLogin(cliente);
                  }}>
                    Alterar Login
                  </Button>
                  
                  {cliente.status === 'ativo' && (
                    <Button size="sm" variant="outline" className="text-yellow-600 border-yellow-600"
                      onClick={() => handleStatusChange(cliente.id, 'bloqueado')}>
                      Bloquear
                    </Button>
                  )}
                  {cliente.status === 'bloqueado' && (
                    <Button size="sm" variant="outline" className="text-green-600 border-green-600"
                      onClick={() => handleStatusChange(cliente.id, 'ativo')}>
                      Desbloquear
                    </Button>
                  )}
                  {cliente.status !== 'cancelado' && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-600"
                      onClick={() => handleStatusChange(cliente.id, 'cancelado')}>
                      Cancelar
                    </Button>
                  )}
                  {cliente.status === 'cancelado' && (
                    <Button size="sm" variant="outline" className="text-green-600 border-green-600"
                      onClick={() => handleStatusChange(cliente.id, 'ativo')}>
                      Reativar
                    </Button>
                  )}
                </div>
                
                {/* Toggle IA Premium */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Assistente IA</p>
                    <p className="text-xs text-gray-400">Recurso premium</p>
                  </div>
                  <button
                    onClick={async () => {
                      const novo = !cliente.ia_assistente_habilitado;
                      try {
                        const token = localStorage.getItem('master_token');
                        await axios.put(`${API}/master/clientes/${cliente.id}/ia-toggle`, 
                          { ia_assistente_habilitado: novo },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setClientes(prev => prev.map(c => c.id === cliente.id ? { ...c, ia_assistente_habilitado: novo } : c));
                        toast.success(novo ? 'IA habilitada' : 'IA desabilitada');
                      } catch { toast.error('Erro ao alterar'); }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      cliente.ia_assistente_habilitado ? 'bg-emerald-500' : 'bg-gray-300'
                    }`}
                    data-testid={`toggle-ia-${cliente.id}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      cliente.ia_assistente_habilitado ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {clientes.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <p className="text-lg">Nenhum cliente cadastrado</p>
              <p className="text-sm">Clique em "Novo Cliente" para começar</p>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {/* Modal: Novo Cliente */}
      <Dialog open={showNovoCliente} onOpenChange={setShowNovoCliente}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Cliente</DialogTitle>
            <DialogDescription>Preencha os dados do cliente e crie o acesso ao app</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNovoCliente}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2 space-y-2">
                <Label>Nome da Clínica *</Label>
                <Input
                  value={formData.nome_clinica}
                  onChange={(e) => setFormData({...formData, nome_clinica: e.target.value})}
                  required
                  data-testid="input-nome-clinica"
                />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input
                  value={formData.cnpj}
                  onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email de Contato</Label>
                <Input
                  type="email"
                  value={formData.email_contato}
                  onChange={(e) => setFormData({...formData, email_contato: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Site</Label>
                <Input
                  value={formData.site}
                  onChange={(e) => setFormData({...formData, site: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Endereço</Label>
                <Input
                  value={formData.endereco}
                  onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-semibold mb-3">Dados de Acesso ao App</h4>
              </div>
              <div className="space-y-2">
                <Label>Email de Login *</Label>
                <Input
                  type="email"
                  value={formData.login_email}
                  onChange={(e) => setFormData({...formData, login_email: e.target.value})}
                  required
                  data-testid="input-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Senha (6 caracteres) *</Label>
                <Input
                  value={formData.login_senha}
                  onChange={(e) => setFormData({...formData, login_senha: e.target.value.slice(0, 6)})}
                  maxLength={6}
                  required
                  placeholder="Ex: abc123"
                  data-testid="input-login-senha"
                />
                <p className="text-xs text-gray-500">{formData.login_senha.length}/6 caracteres</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNovoCliente(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="btn-salvar-cliente">
                {submitting ? 'Salvando...' : 'Criar Cliente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Editar Cliente */}
      <Dialog open={!!showEditCliente} onOpenChange={() => setShowEditCliente(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Atualize os dados do cliente</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCliente}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2 space-y-2">
                <Label>Nome da Clínica *</Label>
                <Input
                  value={formData.nome_clinica}
                  onChange={(e) => setFormData({...formData, nome_clinica: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input
                  value={formData.cnpj}
                  onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email de Contato</Label>
                <Input
                  type="email"
                  value={formData.email_contato}
                  onChange={(e) => setFormData({...formData, email_contato: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Site</Label>
                <Input
                  value={formData.site}
                  onChange={(e) => setFormData({...formData, site: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Endereço</Label>
                <Input
                  value={formData.endereco}
                  onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditCliente(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Alterar Login */}
      <Dialog open={!!showEditLogin} onOpenChange={() => setShowEditLogin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Dados de Login</DialogTitle>
            <DialogDescription>
              Cliente: {showEditLogin?.nome_clinica}<br />
              Login atual: {showEditLogin?.login_email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditLogin}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Novo Email (deixe em branco para manter)</Label>
                <Input
                  type="email"
                  value={loginUpdateData.novo_email}
                  onChange={(e) => setLoginUpdateData({...loginUpdateData, novo_email: e.target.value})}
                  placeholder={showEditLogin?.login_email}
                />
              </div>
              <div className="space-y-2">
                <Label>Nova Senha (6 caracteres - deixe em branco para manter)</Label>
                <Input
                  value={loginUpdateData.nova_senha}
                  onChange={(e) => setLoginUpdateData({...loginUpdateData, nova_senha: e.target.value.slice(0, 6)})}
                  maxLength={6}
                  placeholder="******"
                />
                {loginUpdateData.nova_senha && (
                  <p className="text-xs text-gray-500">{loginUpdateData.nova_senha.length}/6 caracteres</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditLogin(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Salvando...' : 'Atualizar Login'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PainelMasterPage;
